/**
 * GalaxyScoreDisplay Component
 *
 * WHOOP-style animated rings displaying the overall rating score and grade.
 * Features pulsing ring animations and delta comparison.
 *
 * Shared component used by both InstitutionDashboard and Analyst Bench.
 */

import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { getRatingHexColor } from '../../../utils/ratingColors';
import styles from './GalaxyScoreDisplay.module.css';

interface GalaxyScoreDisplayProps {
  /** Current score (0-100) */
  score: number;
  /** Rating grade (e.g., "AA", "BBB") */
  rating: string;
  /** Previous score for delta comparison */
  previousScore: number;
}

export default function GalaxyScoreDisplay({
  score,
  rating,
  previousScore,
}: GalaxyScoreDisplayProps) {
  const { t } = useTranslation();
  const delta = score - previousScore;
  const ratingColor = getRatingHexColor(rating);

  return (
    <div className={styles.galaxyScoreSection}>
      <div className={styles.galaxyContainer}>
        <div className={styles.ring1} />
        <div className={styles.ring2} />
        <div className={styles.galaxyGlow} />
        <div className={styles.scoreCenter}>
          <div className={styles.scoreValue}>{score.toFixed(1)}</div>
          <div className={styles.scoreLabel}>{t('galaxyScore.ratingScore')}</div>
          <div
            className={styles.ratingBadge}
            style={{
              background: `linear-gradient(135deg, ${ratingColor} 0%, ${ratingColor}aa 100%)`,
            }}
          >
            {rating}
          </div>
        </div>
      </div>

      <div className={styles.scoreDelta}>
        <span className={`${styles.delta} ${delta >= 0 ? styles.positive : styles.negative}`}>
          {delta >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          {delta >= 0 ? '+' : ''}
          {delta.toFixed(2)} {t('galaxyScore.vsLastQuarter')}
        </span>
      </div>
    </div>
  );
}
